import { useEffect, useState } from "react";
import { HandCoins, ArrowLeftRight, ArrowRight } from "lucide-react";
import { SCRIPS, MATCHED_TRADES } from "@/data/scrips";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

const Panel = ({ title, icon: Icon, rows, tone, testid }) => (
  <div className="clay-card rounded-[2rem] p-6 sm:p-7" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-lg ${tone === "buy" ? "bg-emerald-500 shadow-emerald-500/40" : "bg-orange-500 shadow-orange-500/40"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="font-heading text-lg font-extrabold text-slate-900">{title}</h3>
      </div>
      <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${tone === "buy" ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-600"}`}>
        {rows.length} open
      </span>
    </div>
    <div className="mt-5 space-y-3">
      {rows.map((o, i) => (
        <div
          key={i}
          data-testid={`${testid}-row-${i}`}
          className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white/70 px-4 py-3"
        >
          <div>
            <div className="flex items-center gap-2">
              {o.scrip && (
                <span className="rounded-full bg-slate-900 px-2 py-0.5 font-mono text-[10px] font-bold text-orange-400">
                  {o.scrip}
                </span>
              )}
              <p className="text-sm font-bold text-slate-800">
                {o.fractions} fraction{o.fractions > 1 ? "s" : ""} @ {inr(o.price)}
              </p>
            </div>
            <p className="mt-0.5 text-[11px] font-semibold text-slate-400">
              {o.member} · {o.ago}
            </p>
          </div>
          <p className={`font-heading text-sm font-black ${tone === "buy" ? "text-emerald-600" : "text-orange-600"}`}>
            {inr(o.fractions * o.price)}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export function OrderBook({ buy, sell, testid = "order-book" }) {
  return (
    <div className="grid gap-6 md:grid-cols-2" data-testid={testid}>
      <Panel title="Offers to Buy" icon={HandCoins} rows={buy} tone="buy" testid={`${testid}-buy`} />
      <Panel title="Offers to Sell" icon={ArrowLeftRight} rows={sell} tone="sell" testid={`${testid}-sell`} />
    </div>
  );
}

export default function LiquidityBoard() {
  const [secs, setSecs] = useState(4);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => (s >= 59 ? 3 : s + 1)), 1000);
    return () => clearInterval(t);
  }, []);

  const buys = SCRIPS.flatMap((s) => s.orders.buy.slice(0, 2).map((o) => ({ ...o, scrip: s.scrip }))).slice(0, 5);
  const sells = SCRIPS.flatMap((s) => s.orders.sell.slice(0, 2).map((o) => ({ ...o, scrip: s.scrip }))).slice(0, 5);

  return (
    <section id="liquidity-board" data-testid="liquidity-board-section" className="py-24">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Liquidity in action</p>
            <h2 className="font-heading mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              A live market for your fractions
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Investors post <span className="font-bold text-slate-800">Offers to Buy</span> and{" "}
              <span className="font-bold text-slate-800">Offers to Sell</span> fractions of listed
              scrips — so your capital is never locked away like a traditional franchise investment.
              A flat <span className="font-bold text-slate-800">₹100 platform fee</span> applies per
              executed trade.
            </p>
          </div>
          <div className="glass-surface flex items-center gap-2.5 rounded-full px-4 py-2" data-testid="board-live-badge">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs font-bold text-slate-600">Live demo board · updated {secs}s ago</span>
          </div>
        </div>

        <div className="mt-12">
          <OrderBook buy={buys} sell={sells} testid="landing-order-book" />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="matched-trades-strip">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Recently matched</span>
          {MATCHED_TRADES.map((m) => (
            <span key={m} className="neu-badge rounded-full px-4 py-1.5 text-xs font-bold text-slate-600">{m}</span>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="#lead-form"
            data-testid="board-join-waitlist-btn"
            className="clay-btn inline-flex items-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-base font-bold text-white"
          >
            Join the waitlist to start trading <ArrowRight className="h-4 w-4" />
          </a>
          <p className="mt-3 text-[10px] text-slate-400">Demo board with simulated orders. Not a live trading venue.</p>
        </div>
      </div>
    </section>
  );
}
